/**
 * The readiness indicator.
 *
 * Colour carries exactly one meaning in this app, and this is it — which is why
 * nothing else on the dashboard is coloured. The band label is always rendered
 * alongside the ring: colour alone is not an accessible signal, and "at risk"
 * versus "action required" is too important to encode in a hue.
 */

import { motion } from 'motion/react';
import { readinessBand, type ReadinessBand } from '@recovery/shared';
import { springStandard } from '../design/motion';
import { useLanguage } from '../i18n/context';

export const BAND_COLOR: Record<ReadinessBand, string> = {
  ready: 'var(--color-ready)',
  due_soon: 'var(--color-due-soon)',
  at_risk: 'var(--color-at-risk)',
  action_required: 'var(--color-action)',
};

export function ReadinessRing({
  score,
  size = 72,
  strokeWidth = 6,
}: {
  score: number;
  size?: number;
  strokeWidth?: number;
}) {
  const { t } = useLanguage();
  const band = readinessBand(score);
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const dash = (Math.max(0, Math.min(100, score)) / 100) * circumference;
  const label = t.readinessBands[band];

  return (
    <div
      className="relative shrink-0"
      style={{ width: size, height: size }}
      role="img"
      aria-label={`${t.common.readiness} ${score} / 100 — ${label}`}
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="var(--color-ink-2)"
          strokeWidth={strokeWidth}
        />
        <motion.circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={BAND_COLOR[band]}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          strokeDasharray={circumference}
          initial={false}
          animate={{ strokeDashoffset: circumference - dash }}
          transition={springStandard}
        />
      </svg>
      <span className="text-numeric absolute inset-0 grid place-items-center text-[0.9375rem] font-[560] text-ink-7">
        {score}
      </span>
    </div>
  );
}

export function BandPill({ score }: { score: number }) {
  const { t } = useLanguage();
  const band = readinessBand(score);
  return (
    <span
      className="text-label inline-flex items-center gap-1.5 rounded-full px-2.5 py-1"
      style={{
        color: BAND_COLOR[band],
        background: `color-mix(in oklab, ${BAND_COLOR[band]} 14%, transparent)`,
      }}
    >
      <span
        className="size-1.5 rounded-full"
        style={{ background: BAND_COLOR[band] }}
        aria-hidden="true"
      />
      {t.readinessBands[band]}
    </span>
  );
}
